import { Movement } from '../../types/movement';
import { Pilgrim } from '../../types/pilgrim';
import { Package } from '../../types/package';
import { Flight } from '../../types/flight';

export const getMovementPilgrims = (
  movement: Movement,
  pilgrims: Record<string, Pilgrim>,
  packages: Record<string, Package>,
  flights: Record<string, Flight>
): Pilgrim[] => {
  // If movement already has pilgrims data, return it
  if (movement.pilgrims && movement.pilgrims.length > 0) {
    return movement.pilgrims;
  }

  // Otherwise, match pilgrims based on movement type
  switch (movement.type) {
    case 'arrival':
      return matchArrivalPilgrims(movement, pilgrims, packages, flights);
    case 'departure':
      return matchDeparturePilgrims(movement, pilgrims, packages, flights);
    case 'hotel_transfer':
    case 'hotel_transfer_madinah':
      return matchHotelTransferPilgrims(movement, pilgrims, packages);
    case 'camp_departure':
    case 'camp_return':
      return matchCampPilgrims(movement, pilgrims, packages);
    default:
      return [];
  }
};

const matchArrivalPilgrims = (
  movement: Movement,
  pilgrims: Record<string, Pilgrim>,
  packages: Record<string, Package>,
  flights: Record<string, Flight>
): Pilgrim[] => {
  return Object.values(pilgrims).filter(pilgrim => {
    if (!pilgrim.PNR_NO) return false;

    const flight = Object.values(flights).find(f => f.pnrNumber === pilgrim.PNR_NO);
    if (!flight) return false;

    const dateMatches = flight.arrivalDate === movement.departureDate;
    const locationMatches = flight.outboundArrival === movement.from.name;

    return dateMatches && locationMatches;
  });
};

const matchDeparturePilgrims = (
  movement: Movement,
  pilgrims: Record<string, Pilgrim>,
  packages: Record<string, Package>,
  flights: Record<string, Flight>
): Pilgrim[] => {
  return Object.values(pilgrims).filter(pilgrim => {
    if (!pilgrim.PNR_NO) return false;

    const flight = Object.values(flights).find(f => f.pnrNumber === pilgrim.PNR_NO);
    if (!flight) return false;

    const dateMatches = flight.returnDate === movement.departureDate;
    const locationMatches = flight.returnDeparture === movement.from.name;

    return dateMatches && locationMatches;
  });
};

const matchHotelTransferPilgrims = (
  movement: Movement,
  pilgrims: Record<string, Pilgrim>,
  packages: Record<string, Package>
): Pilgrim[] => {
  return Object.values(pilgrims).filter(pilgrim => {
    const pkg = Object.values(packages).find(p => p.name === pilgrim.package_nickName);
    if (!pkg) return false;

    if (movement.type === 'hotel_transfer') {
      return pkg.mainMakkahHotel === movement.from.name &&
             pkg.shiftingMakkahHotel === movement.to.name &&
             pkg.shiftingMakkahStartDate === movement.departureDate;
    }

    if (movement.type === 'hotel_transfer_madinah') {
      const fromHotel = pkg.shiftingMakkahHotel || pkg.mainMakkahHotel;
      return fromHotel === movement.from.name &&
             pkg.madinahHotel === movement.to.name &&
             pkg.madinahStartDate === movement.departureDate;
    }

    return false;
  });
};

const matchCampPilgrims = (
  movement: Movement,
  pilgrims: Record<string, Pilgrim>,
  packages: Record<string, Package>
): Pilgrim[] => {
  return Object.values(pilgrims).filter(pilgrim => {
    const pkg = Object.values(packages).find(p => p.name === pilgrim.package_nickName);
    if (!pkg || !pkg.camp) return false;

    if (movement.type === 'camp_departure') {
      const fromHotel = pkg.shiftingMakkahHotel || pkg.mainMakkahHotel;
      return fromHotel === movement.from.name && pkg.camp === movement.to.name;
    }

    if (movement.type === 'camp_return') {
      const toHotel = pkg.shiftingMakkahHotel || pkg.mainMakkahHotel;
      return pkg.camp === movement.from.name && toHotel === movement.to.name;
    }

    return false;
  });
};